import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';
import { WindowElement } from '@/interfaces/components/combinations/WindowElement';

// =====================================================================
// Stateless helpers extracted from CombinationsConfiguratorSelectorPopup
// =====================================================================

export const HERO_PRODUCT_NAMES = ['HERO.1', 'HERO.2', 'HERO.5'];

export function isHeroProductName(productName: string): boolean {
    return HERO_PRODUCT_NAMES.includes(productName);
}

export function getFieldValueByPID(
    rows: CombinationsConfiguratorFormData[],
    productFormId: number,
    fieldPID: string,
): string | number | undefined {
    return rows
        .find((row) => row.activeProductFormId === productFormId)
        ?.formData.find((field) => field.fieldPID === fieldPID)?.fieldValue;
}

export function getWindowDriveValue(
    rows: CombinationsConfiguratorFormData[],
    productFormId: number,
    drivePID: string,
): string {
    const value = getFieldValueByPID(rows, productFormId, drivePID);

    return typeof value === 'string' ? value : '';
}

export function getCombinedWidth({
    rows,
    selectedIds,
    widthPID,
    finalWidthPID,
    dimensionsTypePID,
}: {
    rows: CombinationsConfiguratorFormData[];
    selectedIds: number[];
    widthPID: string;
    finalWidthPID: string;
    dimensionsTypePID: string;
}): number {
    let total = 0;
    const dimensionTypeValueUsedForTotalWidth = 'Dimenzije oken';

    selectedIds.forEach((productId) => {
        const width = getFieldValueByPID(rows, productId, widthPID);
        const finalWidth = getFieldValueByPID(rows, productId, finalWidthPID);
        const dimensionsType = getFieldValueByPID(rows, productId, dimensionsTypePID);

        if (dimensionsType === dimensionTypeValueUsedForTotalWidth && finalWidth !== undefined) {
            total += Number(finalWidth);
            return;
        }

        if (width !== undefined) {
            total += Number(width);
            return;
        }
    });

    return roundToTwoDecimalsIfNeeded(total);
}

function roundToTwoDecimalsIfNeeded(value: number): number {
    return Number.isInteger(value) ? value : Number(value.toFixed(2));
}

export function getWidthDifference(totalWidth: number, combinedWidth: number): number {
    if (totalWidth <= combinedWidth) {
        return 0;
    }

    return totalWidth - combinedWidth;
}

export function areWindowsOfSameHeight(
    rows: CombinationsConfiguratorFormData[],
    idA: number,
    idB: number,
    heightPID: string,
): boolean {
    const a = getFieldValueByPID(rows, idA, heightPID);
    const b = getFieldValueByPID(rows, idB, heightPID);

    if (a === undefined || b === undefined) {
        return false;
    }

    return a === b;
}

export function getPositionDropdownOptions(selectedCount: number): Array<{ label: string; value: string }> {
    if (selectedCount === 2) {
        return [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
        ];
    }

    if (selectedCount === 3) {
        return [
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
        ];
    }

    return [];
}

export function getNoChoiceGuideValue(checklistFieldConfig: any): string {
    return checklistFieldConfig?.LEFT_GUIDE_PID?.noChoiceValue ?? 'Brez';
}

export function getTwoWindowCombinationType(driveOne: string, driveTwo: string, isFirstGuideSingle: boolean): string {
    if (isFirstGuideSingle) {
        return 'D1';
    }

    const drivePatterns = [
        { pattern: () => driveOne === 'L' && driveTwo === 'L', type: 'D2' },
        { pattern: () => driveOne === 'L' && driveTwo === 'D', type: 'D2' },
        { pattern: () => driveOne === 'D' && driveTwo === 'D', type: 'D3' },
    ];

    const match = drivePatterns.find(({ pattern }) => pattern());
    return match ? match.type : '';
}

export interface ThreeWindowGuideFlags {
    areBothGuidesSingle: boolean;
    isSinglePartitionCombination: boolean;
    isPartitionSingleCombination: boolean;
    areBothGuidesPartition: boolean;
}

export function getThreeWindowCombinationType(
    driveOne: string,
    driveTwo: string,
    driveThree: string,
    flags: ThreeWindowGuideFlags,
): string {
    if (flags.areBothGuidesSingle) {
        return 'T1';
    }

    const isAnySinglePartitionCombination = flags.isSinglePartitionCombination || flags.isPartitionSingleCombination;

    const threeWindowPatterns = [
        // T2 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'L' && isAnySinglePartitionCombination,
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'D' && isAnySinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'L' && driveThree === 'L' && flags.isSinglePartitionCombination,
                () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'L' && flags.isPartitionSingleCombination,
            ],
            type: 'T2',
        },
        // T3 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'D' && isAnySinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'L' && driveThree === 'D' && flags.isSinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'D' && isAnySinglePartitionCombination,
                () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'L' && flags.isPartitionSingleCombination,
            ],
            type: 'T3',
        },
        // T4 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'L' && flags.areBothGuidesPartition,
            ],
            type: 'T4',
        },
        // T5 patterns
        {
            patterns: [
                () => driveOne === 'L' && driveTwo === 'L' && driveThree === 'D' && flags.areBothGuidesPartition,
                () => driveOne === 'L' && driveTwo === 'D' && driveThree === 'D' && flags.areBothGuidesPartition,
            ],
            type: 'T5',
        },
        // T6 patterns
        {
            patterns: [
                () => driveOne === 'D' && driveTwo === 'D' && driveThree === 'D' && flags.areBothGuidesPartition,
            ],
            type: 'T6',
        },
    ];

    for (const { patterns, type } of threeWindowPatterns) {
        if (patterns.some((pattern) => pattern())) {
            return type;
        }
    }

    return '';
}

export interface InvalidCombinationParams {
    isFirstGuideSingle: boolean;
    isSecondGuideSingle: boolean;
    isFirstGuidePartition: boolean;
    isSecondGuidePartition: boolean;
    selectedCount: number;
    firstWindowDrive: string;
    secondWindowDrive: string;
    thirdWindowDrive: string;
}

export function isCombinationInvalid(params: InvalidCombinationParams): boolean {
    if ((params.isFirstGuideSingle && params.isSecondGuideSingle) || params.selectedCount === 0) {
        return false;
    }

    if (params.isFirstGuidePartition && params.firstWindowDrive === 'D' && params.secondWindowDrive === 'L') {
        return true;
    }

    if (
        params.isSecondGuidePartition &&
        params.selectedCount === 3 &&
        params.secondWindowDrive === 'D' &&
        params.thirdWindowDrive === 'L'
    ) {
        return true;
    }

    return false;
}

export function computeGuideValueForSegment(
    selectedCount: number,
    requiredCount: number,
    sameHeight: boolean,
    guideType: string,
): string {
    if (selectedCount < requiredCount) {
        return '';
    }

    return sameHeight ? guideType : 'Enojno';
}

export function computeAdjacentLeftGuideValue(rightGuideValue: string, noChoiceGuideValue: string): string {
    if (rightGuideValue === 'Pregradno') {
        return noChoiceGuideValue;
    }

    return rightGuideValue;
}

export function isAssemblyStep1Completed(selectedCount: number): boolean {
    return selectedCount >= 2;
}

export function isAssemblyStep2Completed(
    filteredRows: CombinationsConfiguratorFormData[],
    positionPID: string,
    step1Completed: boolean,
): boolean {
    if (!step1Completed) return false;

    const allHavePositions = filteredRows.every((product) => {
        const positionField = product.formData.find((field) => field.fieldPID === positionPID);
        return !!positionField?.fieldValue;
    });

    if (!allHavePositions) return false;

    const positionValues = filteredRows.map((product) => {
        const positionField = product.formData.find((field) => field.fieldPID === positionPID);
        return positionField?.fieldValue;
    });

    return new Set(positionValues).size === positionValues.length;
}

export function isAssemblyStep3Completed(totalWidth: number, step2Completed: boolean): boolean {
    if (!step2Completed) return false;

    return totalWidth > 0;
}

export function isAssemblyStep4Completed(
    activeStepsCount: number,
    step3Completed: boolean,
    guideType: string,
    filteredRows: CombinationsConfiguratorFormData[],
    drivePID: string,
): boolean {
    if (activeStepsCount < 4) return true;

    if (!step3Completed) return false;
    if (!guideType) return false;

    return filteredRows.every((product) => {
        const driveField = product.formData.find((field) => field.fieldPID === drivePID);
        return !!driveField?.fieldValue;
    });
}

export function computeLargestRollerShutterBoxSize(
    rows: CombinationsConfiguratorFormData[],
    selectedIds: number[],
    boxSizePID: string,
    selectedBoxSizePID: string,
): { targetValue: number | null } {
    if (selectedIds.length === 0) {
        return { targetValue: null };
    }

    const productBoxSizes = selectedIds.map((productId) => {
        const productRow = rows.find((row) => row.activeProductFormId === productId);
        const boxSizeField = productRow?.formData.find((field) => field.fieldPID === boxSizePID);
        const selectedBoxSizeField = productRow?.formData.find((field) => field.fieldPID === selectedBoxSizePID);

        return {
            productId,
            boxSizeValue: boxSizeField?.fieldValue || '',
            selectedBoxSizeValue: selectedBoxSizeField?.fieldValue || '',
        };
    });

    const numericBoxSizes = productBoxSizes
        .map((product) => {
            const boxValue = parseFloat(String(product.boxSizeValue));
            const selectedBoxValue = parseFloat(String(product.selectedBoxSizeValue));
            const validNumValue = isNaN(boxValue) ? selectedBoxValue : boxValue;

            return { productId: product.productId, value: validNumValue };
        })
        .filter((item) => !isNaN(item.value));

    if (numericBoxSizes.length === 0) {
        return { targetValue: null };
    }

    const largest = numericBoxSizes.reduce((max, current) => (current.value > max.value ? current : max));
    return { targetValue: largest.value };
}

export function computeMaxRollerShutterBoxAndSelected(
    rows: CombinationsConfiguratorFormData[],
    selectedIds: number[],
    boxSizePID: string,
    selectedBoxSizePID: string,
): { maxBoxSize: number | null; maxSelectedBoxSize: number | null } {
    const boxSizeValues: number[] = [];
    const selectedBoxSizeValues: number[] = [];

    selectedIds.forEach((productId) => {
        const productRow = rows.find((row) => row.activeProductFormId === productId);
        const boxSizeField = productRow?.formData.find((field) => field.fieldPID === boxSizePID);
        const selectedBoxSizeField = productRow?.formData.find((field) => field.fieldPID === selectedBoxSizePID);

        const boxValue = parseFloat(String(boxSizeField?.fieldValue || ''));
        const selectedValue = parseFloat(String(selectedBoxSizeField?.fieldValue || ''));

        if (!isNaN(boxValue)) boxSizeValues.push(boxValue);
        if (!isNaN(selectedValue)) selectedBoxSizeValues.push(selectedValue);
    });

    return {
        maxBoxSize: boxSizeValues.length > 0 ? Math.max(...boxSizeValues) : null,
        maxSelectedBoxSize: selectedBoxSizeValues.length > 0 ? Math.max(...selectedBoxSizeValues) : null,
    };
}

export function sortSelectedIdsByPosition(
    rows: CombinationsConfiguratorFormData[],
    selectedIds: number[],
    positionPID: string,
): number[] {
    return [...selectedIds].sort((a, b) => {
        const aPositionField = rows
            .find((row) => row.activeProductFormId === a)
            ?.formData.find((field) => field.fieldPID === positionPID);
        const bPositionField = rows
            .find((row) => row.activeProductFormId === b)
            ?.formData.find((field) => field.fieldPID === positionPID);

        if (!aPositionField || !bPositionField) {
            return 0;
        }

        const aPos = Number(aPositionField.fieldValue);
        const bPos = Number(bPositionField.fieldValue);

        return aPos - bPos;
    });
}

/**
 * Orders window elements so that each one with a valid 1-based `canvasConfig.position`
 * claims that slot. Elements without a valid position fill the remaining slots in their
 * original order.
 */
export function sortWindowElementsByPosition(items: WindowElement[]): WindowElement[] {
    const claimed: WindowElement[] = Array.from({ length: items.length });
    const unclaimed: WindowElement[] = [];

    items.forEach((item) => {
        const slot = parseInt(item.canvasConfig.position as any, 10) - 1;
        const canClaimSlot = slot >= 0 && slot < items.length && !claimed[slot];

        if (canClaimSlot) {
            claimed[slot] = item;
        } else {
            unclaimed.push(item);
        }
    });

    return claimed.map((item) => item ?? unclaimed.shift()!);
}

/**
 * Downloads the given Konva stage as a PNG file. Captures the entire stage so
 * that every layer/shape currently rendered in the canvas is encapsulated in
 * the resulting image.
 */
export function downloadStageAsPng(stage: any, fileName: string = 'combined-box.png'): void {
    if (!stage || typeof stage.toCanvas !== 'function') {
        return;
    }

    const stageCanvas = stage.toCanvas({
        pixelRatio: 2,
    });
    const canvas = document.createElement('canvas');
    canvas.width = stageCanvas.width;
    canvas.height = stageCanvas.height;

    const context = canvas.getContext('2d');
    if (!context) {
        return;
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(stageCanvas, 0, 0);

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = fileName.endsWith('.png') ? fileName : `${fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
